import React from "react";

import ErrorIcon from "@mui/icons-material/Error";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import type { DeploymentEvent } from "src/domains/application/hooks/useDeploymentEvents";
import type { PipelineStep as PipelineStateStep } from "src/domains/application/hooks/useDeploymentStream";

import { isStepStale, pulse, statusChipColor } from "./pipeline-tab-helpers";

export interface PipelineHeaderProps {
  status?: string;
  pipelineStatus: string;
  connected: boolean;
  lastEventAt: number | null;
  buildFailed: boolean;
  allCompleted: boolean;
  isRushaOwnedBuild: boolean;
  events: DeploymentEvent[];
  globalFilteredPipelineState: PipelineStateStep[] | null | undefined;
  retryLoading: boolean;
  forceFailLoading: boolean;
  redeployLoading: boolean;
  onRetryBuild: () => void;
  onForceFail: () => void;
  onRedeploy: () => void;
}

export function PipelineHeader({
  status,
  pipelineStatus,
  connected,
  lastEventAt,
  buildFailed,
  allCompleted,
  isRushaOwnedBuild,
  events,
  globalFilteredPipelineState,
  retryLoading,
  forceFailLoading,
  redeployLoading,
  onRetryBuild,
  onForceFail,
  onRedeploy,
}: PipelineHeaderProps): React.ReactElement {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent="space-between"
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {status === "provisioning" || status === "vcs_provisioned"
            ? "Provisioning Pipeline"
            : "Deployment Pipeline"}
        </Typography>
        <Tooltip
          title={
            <Box sx={{ p: 0.5 }}>
              <Typography variant="caption" fontWeight={600} display="block">
                Pipeline terminology
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                <strong>Provisioning</strong> — Connecting your repository and
                setting up infrastructure. Must complete before building.
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                <strong>Building</strong> — Compiling source code into
                deployable images (Docker/Kaniko).
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                <strong>Deploying</strong> — Pushing built images to your
                infrastructure via GitOps (ArgoCD).
              </Typography>
            </Box>
          }
          arrow
          placement="bottom"
        >
          <IconButton size="small" sx={{ ml: 0.5 }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Chip
          label={pipelineStatus}
          size="small"
          color={statusChipColor[pipelineStatus] || "default"}
        />
        {connected ? (
          <Chip
            label="Live"
            size="small"
            color="success"
            variant="outlined"
            sx={
              lastEventAt !== null
                ? {
                    animation: `${pulse} 1.5s ease-in-out 3`,
                  }
                : undefined
            }
          />
        ) : pipelineStatus !== "idle" && pipelineStatus !== "completed" ? (
          <Tooltip title="Live updates paused — reconnecting...">
            <Chip
              icon={<WifiOffIcon />}
              label="Paused"
              size="small"
              color="warning"
              variant="outlined"
            />
          </Tooltip>
        ) : null}
      </Stack>
      {buildFailed && isRushaOwnedBuild && (
        <Tooltip title="Re-queue a build job with the latest commit SHA">
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={
              retryLoading ? (
                <CircularProgress size={14} />
              ) : (
                <ReplayIcon fontSize="small" />
              )
            }
            onClick={onRetryBuild}
            disabled={retryLoading}
          >
            Retry Build
          </Button>
        </Tooltip>
      )}
      {!buildFailed &&
        isStepStale("build", events, globalFilteredPipelineState) &&
        isRushaOwnedBuild && (
          <Tooltip title="Force-fail the stuck build job so you can retry">
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={
                forceFailLoading ? (
                  <CircularProgress size={14} />
                ) : (
                  <ErrorIcon fontSize="small" />
                )
              }
              onClick={onForceFail}
              disabled={forceFailLoading}
            >
              Force Fail
            </Button>
          </Tooltip>
        )}

      {allCompleted && isRushaOwnedBuild && (
        <Tooltip title="Re-build and deploy (full pipeline)">
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={
              redeployLoading ? (
                <CircularProgress size={14} />
              ) : (
                <RocketLaunchIcon fontSize="small" />
              )
            }
            onClick={onRedeploy}
            disabled={redeployLoading}
          >
            Redeploy
          </Button>
        </Tooltip>
      )}
    </Stack>
  );
}
